"use client"

import React, { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Pause, ArrowLeft, HelpCircle, Square, SquareCheckBig, X, Pencil, Check } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { toast } from "sonner"
import LogoHeader from "./functions/header/logo-header"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {exportTextToFile} from '@/app/components/functions/util/export-to-rtf';
import {
  getDefaultSpeechRate,
  stepSpeechRate,
  VoiceSpeechManager,
  VoiceSpeechCallbacks,
  VoiceSpeechState,
} from '@/app/components/functions/util/voice-speech';
import {
  DEFAULT_SPEECH_LANGUAGE,
  voiceMatchesLanguage,
} from '@/app/components/functions/util/speech-language';
import { splitDisplayWords } from '@/app/components/functions/util/speech-alignment';
import SpeechBar from '@/app/components/speech-bar';
import { findSentenceBoundaries } from '@/app/components/functions/util/text-util';
import { breakSentencesAtAbbreviationSafePeriods } from '@/app/components/functions/util/sentence-breaks';
import DebugLogPanel from '@/app/components/functions/debug/debug-log-panel';
import { isVoiceSpeechSupported } from '@/app/components/functions/util/app-type-util';
import { PAGE_BREAK_TOKEN } from '@/app/components/functions/util/constants';
import {
  buildChunkedTextRuns,
  stripImageBlockMarkers,
  type ChunkedTextRun,
} from '@/app/components/functions/util/chunked-text-runs';
import { fromEditableText, toEditableText, type EditableText } from '@/app/components/functions/util/editable-text';
import type { OcrSummary } from '@/app/components/functions/util/ocr-types';
import {
  COLOR_PRESETS,
  colorPresetIndex,
  DEFAULT_SETTINGS,
  FIXED_TEXT_STYLES,
  LINE_SPACINGS,
  loadSettings,
  paletteIndex,
  resolveSpeechRate,
  saveSettings,
  TextDisplaySettings
} from '@/app/components/functions/util/text-display-settings';
import { logger } from "@/lib/logger";
import { ExportIcon, SpeakerIcon, TextFormatIcon } from "@/app/components/functions/icons/app-icons";
import HelpOverlay from "@/app/components/functions/help/help-overlay";

// Component for rendering a nice visual page separator
const PageBreakSeparator = ({ pageNumber }: { pageNumber: number }) => (
  <div className="flex items-center justify-center my-8 select-none">
    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-gray-300" />
    <div className="mx-4 flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 border border-gray-200 shadow-sm">
      <svg 
        className="w-4 h-4 text-gray-400" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          strokeWidth={2} 
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
        />
      </svg>
      <span className="text-sm font-medium text-gray-500">Strona {pageNumber + 1}</span>
    </div>
    <div className="flex-1 h-px bg-gradient-to-l from-transparent via-gray-300 to-gray-300" />
  </div>
);

// Component for rendering text extracted from images/diagrams
// Simple inline design: just a small image icon before the text
const ImageContentBlock = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-baseline gap-1.5">
    <svg 
      className="w-4 h-4 opacity-50 flex-shrink-0 self-center" 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
      aria-label="Tekst z obrazu"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
      />
    </svg>
    <span>{children}</span>
  </span>
);

// Component for rendering main headings (## text ##)
// Larger and bolder for clear visual hierarchy
const MainHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-2xl font-bold mt-8 mb-4 text-inherit">
    {children}
  </h2>
);

// Component for rendering subheadings (### text ###)
// Medium size, semibold for secondary hierarchy
const SubHeading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-xl font-semibold mt-6 mb-3 text-inherit">
    {children}
  </h3>
);

// Component for rendering bullet list items (* text)
// Proper bullet symbol with indentation
const BulletListItem = ({ children }: { children: React.ReactNode }) => (
  <div className="flex gap-3 my-1 ml-2">
    <span className="select-none text-inherit flex-shrink-0">•</span>
    <span className="flex-1">{children}</span>
  </div>
);

/**
 * Emphasis the page carried, rendered as weight.
 *
 * Deliberately `font-bold` whatever the source did. The British Dyslexia
 * Association's style guidance is to avoid italics and carry emphasis with weight
 * instead, so a faithfully reproduced italic passage would be a correctly
 * implemented regression. The OCR prompt marks bold and italic with the same
 * token for the same reason: what is recorded is *that the source emphasised this
 * span*, and how that appears belongs to the reader.
 */
const Emphasis = ({ children }: { children: React.ReactNode }) => (
  <strong className="font-bold text-inherit">{children}</strong>
);

/**
 * A web or e-mail address, kept out of the hyphenator.
 *
 * `hyphens: auto` breaks a long address by hyphenating it, and the hyphen it puts
 * in cannot be told apart from one that belongs to the address: a reader copying
 * `nobel_prize_mathe-r.html` off the screen copies a character that was never in
 * the document. Unhyphenatable is not unbreakable - the text still wraps
 * `anywhere`, so a footnote URL fits a phone, just without the invented hyphen.
 */
const Address = ({ children }: { children: React.ReactNode }) => (
  <span style={{ hyphens: 'none', WebkitHyphens: 'none' }}>{children}</span>
);

/*
 * A token carrying a slash, an at-sign, or a leading `www.`.
 *
 * Loose on purpose. The chunker that drives playback splits on sentence
 * punctuation, and a URL is full of full stops, so during playback this sees
 * pieces of an address rather than the whole of one - `http://www.`, then
 * `nasa.`, then the long slashed remainder. Every piece long enough for the
 * hyphenator to touch still carries a slash, which a matcher anchored on the
 * scheme would have missed. It also catches `and/or` and `1/100,000`, and
 * marking those costs nothing: hyphens:none only shows on a token the
 * hyphenator would otherwise have broken.
 */
const ADDRESS_TOKEN = /((?:www\.|\S*[/@])\S*)/gi

/**
 * A run of text with its addresses marked.
 *
 * Returns the run untouched when it holds none, which is almost every run: this
 * sees every fragment of every page, and on all but a handful the split is all
 * the work it does.
 */
const renderAddresses = (text: string, keyPrefix: string): React.ReactNode => {
  const parts = text.split(ADDRESS_TOKEN)
  if (parts.length === 1) return text

  // One capturing group, so split() leaves the addresses at the odd indices.
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <Address key={`${keyPrefix}-a${index}`}>{part}</Address>
    ) : (
      <React.Fragment key={`${keyPrefix}-p${index}`}>{part}</React.Fragment>
    )
  )
}

/**
 * Split a run of text on **emphasis** markers.
 *
 * Inline rather than block-level, so it cannot use the same split as headings and
 * bullets - it runs inside each part that survives that pass, including the text
 * of a heading or a bullet.
 *
 * No conflict with the bullet marker: that pattern requires whitespace after the
 * asterisk (`^\*\s+`), and `**` never has any.
 */
const renderInlineEmphasis = (text: string, keyPrefix: string): React.ReactNode => {
  if (!text.includes('**')) return renderAddresses(text, keyPrefix)
  return text.split(/(\*\*[^*]+\*\*)/g).map((chunk, index) => {
    const match = chunk.match(/^\*\*([^*]+)\*\*$/)
    if (match) {
      return (
        <Emphasis key={`${keyPrefix}-e${index}`}>
          {renderAddresses(match[1], `${keyPrefix}-e${index}`)}
        </Emphasis>
      )
    }
    return (
      <React.Fragment key={`${keyPrefix}-t${index}`}>
        {renderAddresses(chunk, `${keyPrefix}-t${index}`)}
      </React.Fragment>
    )
  })
}

/**
 * Perceived brightness of a background colour, used to pick a highlight that
 * stays legible.
 *
 * The reader chooses from nine colour presets, two of which are dark - #1B1B1B
 * and #0B1F3A - and the highlight has to work on all of them without touching the
 * text colour: a highlight that repaints the text is a highlight that can put
 * dark on dark.
 */
const isDarkBackground = (color: string): boolean => {
  const hex = color.replace('#', '')
  if (hex.length !== 6) return false

  const red = Number.parseInt(hex.slice(0, 2), 16)
  const green = Number.parseInt(hex.slice(2, 4), 16)
  const blue = Number.parseInt(hex.slice(4, 6), 16)

  // Rec. 601 luma, which is close enough for a light/dark decision.
  return (0.299 * red + 0.587 * green + 0.114 * blue) / 255 < 0.5
}

/**
 * Geometry shared by every word, highlighted or not.
 *
 * The rounded padding is on all of them and cancelled by an equal negative
 * margin, so the highlight has room to breathe and yet moving it changes no
 * layout at all. Only the background colour transitions, which is what makes the
 * highlight glide from word to word instead of jumping - the previous version
 * animated `all`, including the padding and margin it toggled, so every step
 * reflowed the paragraph. The chunk highlight was also `inline-block`, an atomic
 * box that cannot start mid-line, so a highlighted sentence was pushed onto a
 * line of its own and the whole page moved under the reader.
 */
const SPEECH_WORD_STYLE: React.CSSProperties = {
  borderRadius: '4px',
  padding: '0 2px',
  margin: '0 -2px',
  transition: 'background-color 140ms ease-out',
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
}

const SPEECH_CHUNK_STYLE: React.CSSProperties = {
  borderRadius: '4px',
  transition: 'background-color 220ms ease-out',
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
}

interface TextDisplayScreenProps {
  text: string
  ocrProvider?: 'gemini' | 'tesseract' | null
  ocrSummary?: OcrSummary | null
  onBackToHome: () => void
  onNavigateToHome: () => void
  onRetryOcr?: () => void
}

// R9 asked for 24. The maximum was 22, not the 18 the report assumed, so this is
// one appended value: both sliders are range inputs over the array index, and a
// saved size outside the list is normalised on load, so growing them is safe for
// people who already have a size stored. Shrinking them would not be. DYS-033.
const FONT_SIZES = [16, 18, 20, 22, 24]
// Three tiles across, matching grid-cols-3 on the colour grid below. The arrow
// keys need the row width to step up and down, and nine divides by it, so a
// column wraps onto itself.
const COLOR_PRESET_COLUMNS = 3
const FONT_OPTIONS = [
  { name: "Verdana", value: "Verdana, sans-serif" },
  { name: "Arial", value: "Arial, sans-serif" },
  { name: "Tahoma", value: "Tahoma, sans-serif" },
  { name: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
  { name: "Calibri", value: "Calibri, sans-serif" },
  { name: "Open Sans", value: "var(--font-open-sans), Arial, sans-serif" },
]

export default function TextDisplayScreen({ text, ocrProvider, ocrSummary, onBackToHome, onNavigateToHome, onRetryOcr }: TextDisplayScreenProps) {
  
  // Emergency speech stop - runs immediately on component mount
  useEffect(() => {
    const emergencyStopSpeech = () => {
      try {
        if (voiceSpeechManagerRef.current) {
          voiceSpeechManagerRef.current.stop()
        } else {
          speechSynthesis.cancel()
        }
      } catch (error) {
        console.warn('Emergency speech stop error:', error)
      }
    }

    // Set up emergency stops for actual page unload (not tab switching)
    const events = ['beforeunload', 'unload', 'pagehide']
    events.forEach(event => {
      window.addEventListener(event, emergencyStopSpeech, { capture: true, passive: true })
      document.addEventListener(event, emergencyStopSpeech, { capture: true, passive: true })
    })
    
    // Handle visibilitychange separately to distinguish tab switching from page unload
    // (visibilitychange is handled in the separate useEffect below)

    // Periodic check only for actual page transitions, not tab switching
    // (Tab switching is handled by the visibilitychange handler below)

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, emergencyStopSpeech, { capture: true })
        document.removeEventListener(event, emergencyStopSpeech, { capture: true })
      })
    }
  }, [])
  const [isPlaying, setIsPlaying] = useState(false)
  const [fontSize, setFontSize] = useState(DEFAULT_SETTINGS.fontSize)
  const [lineSpacing, setLineSpacing] = useState(DEFAULT_SETTINGS.lineSpacing)
  const [backgroundColor, setBackgroundColor] = useState(DEFAULT_SETTINGS.backgroundColor)
  const [textColor, setTextColor] = useState(DEFAULT_SETTINGS.textColor)
  const [fontFamily, setFontFamily] = useState(DEFAULT_SETTINGS.fontFamily)
  const [newSentenceNewLine, setNewSentenceNewLine] = useState(DEFAULT_SETTINGS.newSentenceNewLine)
  const [ttsVoiceURI, setTtsVoiceURI] = useState(DEFAULT_SETTINGS.ttsVoiceURI)
  const [ttsRate, setTtsRate] = useState(DEFAULT_SETTINGS.ttsRate)
  /**
   * Whether this browser tells us which word is being spoken. See
   * VoiceSpeechState.wordHighlightAvailable: where it does not, the chunk is the
   * smallest thing that can be highlighted.
   */
  const [wordHighlightAvailable, setWordHighlightAvailable] = useState(false)
  /** The speech bar opens on the first play and stays until the reader closes it. */
  const [showSpeechBar, setShowSpeechBar] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([])
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [currentWordIndex, setCurrentWordIndex] = useState(-1)
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(-1)
  const [wasPlayingBeforeHidden, setWasPlayingBeforeHidden] = useState(false)
  /*
   * The editor sees the text, not the markup.
   *
   * This textarea used to bind straight to the display text, so tapping "Edytuj"
   * showed `<STRONA>` between every page and, once emphasis shipped, `**` around
   * every bold span. Raw markers in front of a dyslexic reader are the thing this
   * app exists to avoid - and they were editable, so deleting one quietly removed a
   * page separator from the reader and a section break from the RTF export, with
   * nothing to connect the two.
   *
   * The markers are stripped on the way in and put back on the way out. Anything
   * that can no longer be located is dropped rather than guessed: a page separator
   * in the wrong place is worse than a missing one. See editable-text.ts.
   *
   * This is not the fix for the token itself - it still lives in the text
   * everywhere else. See docs/task-p1.6-page-breaks.md.
   */
  const [editableMarkers, setEditableMarkers] = useState<EditableText | null>(null)

  const [isEditing, setIsEditing] = useState(false)
  const [currentDisplayText, setCurrentDisplayText] = useState(text)
  const [originalTextOnEditStart, setOriginalTextOnEditStart] = useState("")
  const [isPaused, setIsPaused] = useState(false)
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false)
  const [showTesseractNotice, setShowTesseractNotice] = useState(true) // Show notice by default if tesseract was used
  const [hasDownloaded, setHasDownloaded] = useState(false) // Track if user has exported the text
  const hasDownloadedRef = useRef(false) // Ref for access in beforeunload handler
  const pendingNavigationRef = useRef<'back' | 'home' | null>(null)
  const [debugLogs, setDebugLogs] = useState<string[]>([])
  const [voiceSpeechState, setVoiceSpeechState] = useState<VoiceSpeechState>({
    isPlaying: false,
    isPaused: false,
    currentWordIndex: -1,
    currentSentenceIndex: -1,
    currentDisplayText: '',
    language: DEFAULT_SPEECH_LANGUAGE,
    wordHighlightAvailable: false,
    rate: getDefaultSpeechRate(),
  })
  const [isVoiceSpeechSupportedState, setIsVoiceSpeechSupportedState] = useState(true)
  const textContainerRef = useRef<HTMLDivElement | null>(null)
  /**
   * What the scroll-into-view effect follows: the word being spoken where the
   * engine reports one, otherwise the chunk.
   */
  const highlightedWordRef = useRef<HTMLSpanElement | null>(null)
  const isIOSRef = useRef<boolean>(false)
  const isMobileRef = useRef<boolean>(false)
  const voiceSpeechStateRef = useRef<VoiceSpeechState>({
    isPlaying: false,
    isPaused: false,
    currentWordIndex: -1,
    currentSentenceIndex: -1,
    currentDisplayText: '',
    language: DEFAULT_SPEECH_LANGUAGE,
    wordHighlightAvailable: false,
    rate: getDefaultSpeechRate(),
  })
  
  // Initialize voice speech manager
  const voiceSpeechManagerRef = useRef<VoiceSpeechManager | null>(null)
  
  const addDebugLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString()
    const logMessage = `[${timestamp}] ${message}`
    console.log(logMessage)
    setDebugLogs(prev => [...prev.slice(-9), logMessage]) // Keep last 10 logs
  }, [])

  // Check browser support for voice speech
  useEffect(() => {
    setIsVoiceSpeechSupportedState(isVoiceSpeechSupported())
  }, [])

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = loadSettings()
    const allowedFontValues = new Set(FONT_OPTIONS.map((option) => option.value))
    const normalizedFontFamily =
      savedSettings.fontFamily && allowedFontValues.has(savedSettings.fontFamily)
        ? savedSettings.fontFamily
        : DEFAULT_SETTINGS.fontFamily
    const normalizedFontSize = FONT_SIZES.includes(savedSettings.fontSize)
      ? savedSettings.fontSize
      : DEFAULT_SETTINGS.fontSize
    setFontFamily(normalizedFontFamily)
    setFontSize(normalizedFontSize)
    setLineSpacing(savedSettings.lineSpacing)
    // Always one of COLOR_PRESETS: loadSettings normalises the pair, and migrates
    // anything saved before the tiles existed.
    setTextColor(savedSettings.textColor)
    setBackgroundColor(savedSettings.backgroundColor)
    setNewSentenceNewLine(savedSettings.newSentenceNewLine)
    setTtsVoiceURI(savedSettings.ttsVoiceURI || DEFAULT_SETTINGS.ttsVoiceURI)
    setTtsRate(savedSettings.ttsRate)
    setSettingsLoaded(true)
  }, [])

  // Save settings to localStorage when they change (after initial load)
  useEffect(() => {
    if (!settingsLoaded) return // Don't save during initial load
    
    const currentSettings: TextDisplaySettings = {
      fontFamily,
      fontSize,
      lineSpacing,
      textColor,
      backgroundColor,
      newSentenceNewLine,
      ttsRate,
      /*
       * All three still written, though nothing reads any of them any more.
       *
       * The stored settings are one object under one key, so dropping a field from
       * what is saved rewrites that object without it - and ttsRate, the reader's
       * saved speed, is in the same object. Keeping the shape intact is what keeps
       * the speed across the change. 'auto' because that is now true: no setting
       * overrides the language, which is fixed to Polish at the manager. 'off'
       * likewise: the colour tiles replaced the high-contrast modes, and writing
       * the field back off is what keeps normalizeSettings from migrating an
       * already-migrated pair on the next load.
       *
       * The voice is saved again, and the reason it briefly was not is worth
       * keeping. When the picker was removed, a URI chosen while it existed stayed
       * in storage and selectVoice honours a preferred voice BEFORE it ranks
       * anything - so the saved voice outlived the control that set it, the
       * Google-first rule never reached that reader, and nothing on screen could
       * undo it. The cure was to overwrite it. The picker is back at the client's
       * request (DYS-053), so the reader can change it again and persisting it is
       * correct once more. Do not reinstate the overwrite without also removing
       * the control. DYS-044, DYS-047, DYS-053.
       */
      ttsVoiceURI,
      ttsLanguage: DEFAULT_SETTINGS.ttsLanguage,
      highContrast: DEFAULT_SETTINGS.highContrast,
    }
    saveSettings(currentSettings)
  }, [settingsLoaded, fontFamily, fontSize, lineSpacing, textColor, backgroundColor, newSentenceNewLine, ttsVoiceURI, ttsRate])

  useEffect(() => {
    const callbacks: VoiceSpeechCallbacks = {
      setState: (newState) => {
        // Update all individual state variables for component reactivity
        setIsPlaying(newState.isPlaying)
        setIsPaused(newState.isPaused)
        setCurrentWordIndex(newState.currentWordIndex)
        setCurrentSentenceIndex(newState.currentSentenceIndex)
        setWordHighlightAvailable(newState.wordHighlightAvailable)
        setVoiceSpeechState(newState)
      },
      debugLog: addDebugLog,
    }

    // Enable verbose TTS debug logs only outside production.
    const debugEnabled = process.env.NODE_ENV !== 'production'
    const initialState: VoiceSpeechState = {
      isPlaying: false,
      isPaused: false,
      currentWordIndex: -1,
      currentSentenceIndex: -1,
      currentDisplayText: text,
      language: DEFAULT_SPEECH_LANGUAGE,
      wordHighlightAvailable: false,
      rate: getDefaultSpeechRate(),
    }
    voiceSpeechManagerRef.current = new VoiceSpeechManager(initialState, callbacks, debugEnabled)
    voiceSpeechManagerRef.current.setPreferredVoiceURI(ttsVoiceURI)
    // Polish, decided here and never again: the app reads Polish only, so there is
    // no setting to thread through the screen and nothing for detection to get
    // wrong. The other languages are still implemented - see speech-language.ts -
    // and bringing them back means putting a setting behind this one call rather
    // than rebuilding anything. DYS-044.
    voiceSpeechManagerRef.current.setLanguageSetting('pl')
    
    // Test that debug logging is connected (only if debug is enabled)
    if (voiceSpeechManagerRef.current && debugEnabled) {
      addDebugLog("[VoiceSpeech] Manager initialized - debug logging connected")
    }

    return () => {
      // additional cleanup handled in dedicated unmount effect below
    }
  }, [addDebugLog])

  useEffect(() => {
    if (voiceSpeechManagerRef.current) {
      voiceSpeechManagerRef.current.setPreferredVoiceURI(ttsVoiceURI)
    }
  }, [ttsVoiceURI])

  // The saved speed is a stored preference; 0 means "this device's default".
  useEffect(() => {
    if (voiceSpeechManagerRef.current) {
      voiceSpeechManagerRef.current.setRate(resolveSpeechRate({ ttsRate }))
    }
  }, [ttsRate])

  // Keep hasDownloadedRef in sync with state (needed for beforeunload handler)
  useEffect(() => {
    hasDownloadedRef.current = hasDownloaded
  }, [hasDownloaded])

  // Sync component state with voiceSpeechStateRef for VoiceSpeechManager
  useEffect(() => {
    voiceSpeechStateRef.current = {
      ...voiceSpeechStateRef.current,
      isPlaying,
      isPaused,
      currentWordIndex,
      currentSentenceIndex,
      currentDisplayText
    }
  }, [isPlaying, isPaused, currentWordIndex, currentSentenceIndex, currentDisplayText])

  const fontSizes = FONT_SIZES
  const lineSpacings = LINE_SPACINGS

  /*
   * What the reader sees is what is saved, with nothing in between.
   *
   * There used to be a resolveDisplayColors here, because a high-contrast mode
   * overrode the pair for display while leaving the two slider selections
   * untouched underneath it. A tile writes both halves itself, so there is
   * nothing left to override and no second pair to keep in step.
   */

  /**
   * The tile the reader is on, derived rather than stored.
   *
   * The pair is what goes to localStorage, so there is no index beside it that
   * could fall out of step with it - and a later edit to COLOR_PRESETS cannot
   * silently repaint a saved choice. colorPresetIndex never returns -1, so the
   * grid always has exactly one checked tile, and therefore exactly one tab stop.
   */
  const selectedColorPresetIndex = colorPresetIndex({ textColor, backgroundColor })

  /** One per tile, so an arrow key can move focus onto the tile it just picked. */
  const colorPresetRefs = useRef<Array<HTMLButtonElement | null>>([])

  const selectColorPreset = (index: number) => {
    const preset = COLOR_PRESETS[index]
    setTextColor(preset.textColor)
    setBackgroundColor(preset.backgroundColor)
  }

  /**
   * Arrow keys move and select in one step, which is the radio-group pattern:
   * the reader hears the pair named and watches the page repaint as they go,
   * rather than arrowing to a tile and having to press space to find out what it
   * does.
   */
  const handleColorPresetKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const count = COLOR_PRESETS.length
    let next: number

    switch (event.key) {
      case 'ArrowRight':
        next = (index + 1) % count
        break
      case 'ArrowLeft':
        next = (index - 1 + count) % count
        break
      // A row is COLOR_PRESET_COLUMNS tiles, and the grid is exactly three rows,
      // so up and down wrap within the reader's own column.
      case 'ArrowDown':
        next = (index + COLOR_PRESET_COLUMNS) % count
        break
      case 'ArrowUp':
        next = (index - COLOR_PRESET_COLUMNS + count) % count
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = count - 1
        break
      default:
        return
    }

    // Otherwise the same arrow scrolls the drawer out from under the grid.
    event.preventDefault()
    selectColorPreset(next)
    colorPresetRefs.current[next]?.focus()
  }

  useEffect(() => {
    // When text prop changes (e.g., from parent), stop speech (not pause - cannot resume)
    if (voiceSpeechManagerRef.current) {
      voiceSpeechManagerRef.current.stop()
      voiceSpeechManagerRef.current.updateDisplayText(text)
    }
    setCurrentDisplayText(text)
  }, [text])

  useEffect(() => {
    return () => {
      // Aggressive cleanup on component unmount
      if (voiceSpeechManagerRef.current) {
        voiceSpeechManagerRef.current.stop()
      }
      // Always cancel speech synthesis as backup
      speechSynthesis.cancel()
    }
  }, [])

  // Detect iOS and mobile devices
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase()
    isIOSRef.current = /iphone|ipad|ipod/.test(userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    
    // Detect mobile devices (including Android, etc.)
    isMobileRef.current = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent) ||
      (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform))
  }, [])

  // Load voices when available (voices might not be ready immediately)
  useEffect(() => {
    const loadVoices = () => {
      // Trigger voice loading
      const voices = speechSynthesis.getVoices()
      /*
       * The list behind the voice picker, and the nudge the manager needs.
       *
       * Filtered to Polish, which is the only language this app reads - the
       * language picker is gone for good and only the voice came back (DYS-053).
       * voiceMatchesLanguage rather than a prefix test written again here: it also
       * accepts the three-letter tags Android reports ("pol") and a voice whose tag
       * says nothing but whose name does. A prefix test drops both, which on a
       * phone can mean dropping every voice the device has - hence the fallback to
       * the full list when the filter finds nothing.
       */
      const deduplicated = new Map<string, SpeechSynthesisVoice>()
      for (const voice of voices) {
        // Some Android builds report the same voice more than once.
        deduplicated.set(voice.voiceURI || `${voice.name}|${voice.lang}`, voice)
      }
      const uniqueVoices = [...deduplicated.values()]

      const polishVoices = uniqueVoices
        .filter((voice) => voiceMatchesLanguage(voice, DEFAULT_SPEECH_LANGUAGE))
        .sort((a, b) => a.name.localeCompare(b.name, 'pl'))
      setAvailableVoices(polishVoices.length > 0 ? polishVoices : uniqueVoices)

      // A list that grew has to reach the engine too, or the voice cached from the
      // short early list keeps being used.
      voiceSpeechManagerRef.current?.refreshVoices()

      // The deduplicated count, not the raw one: on the Android builds that report
      // a voice twice, the raw number says the list grew when it did not.
      console.log(`Voice loading attempt: found ${deduplicated.size} voices`)
      
      // iOS-specific voice loading improvements
      if (isIOSRef.current && voices.length === 0) {
        console.log('iOS detected with no voices - forcing voice load with dummy utterance')
        // Create a dummy utterance to force iOS to load voices
        const dummyUtterance = new SpeechSynthesisUtterance('')
        dummyUtterance.lang = 'pl-PL'
        dummyUtterance.volume = 0 // Silent
        speechSynthesis.speak(dummyUtterance)
        speechSynthesis.cancel()
      }
    }
    
    // Load voices immediately
    loadVoices()
    
    /*
     * Kept listening for as long as the screen is open.
     *
     * addEventListener rather than assigning onvoiceschanged: that property has one
     * slot, so anything else that assigns it - another component, an extension -
     * silently replaced this and the list stopped refreshing. And the retries below
     * stop after a couple of seconds, so a voice list that arrives late, or arrives
     * after the tab was backgrounded through the load, used to be missed for the
     * rest of the session. That is what made the picker's contents depend on timing.
     */
    const handleVoicesChanged = () => {
      console.log('Voices changed event fired')
      loadVoices()
    }

    if (typeof speechSynthesis.addEventListener === 'function') {
      speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged)
    } else if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = handleVoicesChanged
    }

    // Coming back to the tab is a good moment to look again: engines populate the
    // list lazily and a backgrounded tab may never have been given it.
    const handleVisible = () => {
      if (!document.hidden) loadVoices()
    }
    document.addEventListener('visibilitychange', handleVisible)
    
    /*
     * Retries, with a cleanup.
     *
     * Engines populate the voice list some time after the page loads, and on iOS
     * that can be seconds. The cleanup cancels whatever is still pending, so a
     * screen closed before the engine answered leaves no timers calling into it.
     */
    const retryTimers: ReturnType<typeof setTimeout>[] = []
    const retryAfter = (delays: number[]) => {
      for (const delay of delays) {
        retryTimers.push(setTimeout(loadVoices, delay))
      }
    }

    const handleFirstInteraction = () => {
      console.log('First user interaction - loading voices for iOS')
      loadVoices()
      // Remove the listener after first use
      document.removeEventListener('touchstart', handleFirstInteraction)
      document.removeEventListener('click', handleFirstInteraction)
    }

    // iOS-specific loading with more attempts and longer delays
    if (isIOSRef.current) {
      console.log('iOS detected - using extended voice loading strategy')
      retryAfter([100, 300, 500, 1000, 2000]) // iOS can be very slow to load voices

      // Also try to load voices when the user first interacts with the page
      document.addEventListener('touchstart', handleFirstInteraction, { once: true })
      document.addEventListener('click', handleFirstInteraction, { once: true })
    } else {
      // Non-iOS devices
      retryAfter([100, 500, 1000])
    }

    return () => {
      for (const timer of retryTimers) {
        clearTimeout(timer)
      }
      document.removeEventListener('touchstart', handleFirstInteraction)
      document.removeEventListener('click', handleFirstInteraction)
      document.removeEventListener('visibilitychange', handleVisible)
      if (typeof speechSynthesis.removeEventListener === 'function') {
        speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged)
      } else if (speechSynthesis.onvoiceschanged === handleVoicesChanged) {
        speechSynthesis.onvoiceschanged = null
      }
    }
  }, [])

  // Prevent body scrolling on iOS to fix header disappearing issue
  useEffect(() => {
    // Set CSS custom property for real viewport height (iOS Safari fix)
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }
    
    // Set initial value
    setViewportHeight()
    
    // Update on resize (iOS Safari address bar changes)
    window.addEventListener('resize', setViewportHeight)
    window.addEventListener('orientationchange', setViewportHeight)
    
    // Prevent document body from scrolling
    const originalStyle = window.getComputedStyle(document.body).overflow
    document.body.style.overflow = 'hidden'
    
    // Also prevent scrolling on the document element for iOS
    const originalDocumentStyle = window.getComputedStyle(document.documentElement).overflow
    document.documentElement.style.overflow = 'hidden'
    
    // Prevent touch scrolling on iOS
    const preventTouchMove = (e: TouchEvent) => {
      // Only prevent if the touch is on the body/html, not on scrollable content
      const target = e.target as Element
      if (!target.closest('.overflow-y-auto')) {
        e.preventDefault()
      }
    }
    
    document.addEventListener('touchmove', preventTouchMove, { passive: false })
    
    return () => {
      document.body.style.overflow = originalStyle
      document.documentElement.style.overflow = originalDocumentStyle
      window.removeEventListener('resize', setViewportHeight)
      window.removeEventListener('orientationchange', setViewportHeight)
      document.removeEventListener('touchmove', preventTouchMove)
    }
  }, [])

  const handlePlayPause = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (!isVoiceSpeechSupportedState) {
      alert("Czytanie tekstu nie jest obsłużone w tej przeglądarce. Użyj przeglądarki Google Chrome lub Microsoft Edge")
      return
    }
    
    if (!voiceSpeechManagerRef.current) {
      return
    }
    
    // Only allow starting playback via a trusted user gesture (real click/tap).
    // Prevents TTS from auto-starting when the text screen opens (e.g. visibility/focus quirks).
    if (!isPlaying && e?.isTrusted !== true) {
      return
    }

    // The bar opens with the first play and then stays: the speed and the voice
    // are things a reader adjusts while listening, so they have to outlive the
    // playback that revealed them.
    if (!isPlaying) {
      setShowSpeechBar(true)
    }

    voiceSpeechManagerRef.current.handlePlayPause()
  }

  const handleSkipPrevious = () => {
    voiceSpeechManagerRef.current?.skipToPreviousChunk()
  }

  const handleSkipNext = () => {
    voiceSpeechManagerRef.current?.skipToNextChunk()
  }

  /*
   * Whether a tap on a sentence reads from there. DYS-045.
   *
   * Armed only once the speech bar is open, which is to say only after the
   * reader has pressed play at least once in this document. That is the client's
   * decision and it is the right one: on a phone, a document that starts talking
   * because a thumb brushed a line is a defect rather than a feature, and the
   * reader has no way to know a tap would do that until they have heard the app
   * speak at all. Opening a document arms nothing.
   *
   * Never while editing: that branch is a textarea, where a tap means the caret
   * goes here. The reading container is not even mounted then, so this is the
   * rule stated where it can be read rather than inferred from the JSX.
   *
   * Never where the browser cannot speak. In practice the bar cannot be open
   * there either, since handlePlayPause refuses first, but the two conditions are
   * set in different places and this keeps them from drifting apart.
   */
  const readFromTapArmed = showSpeechBar && !isEditing && isVoiceSpeechSupportedState

  /*
   * Tap a sentence, hear it from there.
   *
   * One handler on the reading container rather than one per chunk: a long
   * document is hundreds of chunks, and hundreds of listeners to attach, hold and
   * tear down on every re-render - of which playback produces one per spoken
   * word. The tapped chunk is recovered from the DOM instead, by the
   * data-chunk-index the chunk span carries.
   *
   * Keyboard access: deliberately none on the chunks themselves.
   *
   * A click handler on a div is not reachable from a keyboard, and the usual
   * remedy - tabIndex and a role on every chunk - would drop several hundred tab
   * stops into a seventeen-page document, between the reader and every other
   * control on the screen. That is a heavier barrier than the one it removes, and
   * it is not what WCAG 2.1.1 asks for: the requirement is that the *function* be
   * available from a keyboard, not that this particular element be focusable.
   *
   * The function already is. "Poprzedni fragment" and "Następny fragment" in the
   * speech bar step through exactly these chunks, are ordinary focusable buttons
   * with Polish labels, and are on screen whenever this gesture is armed - the
   * bar being open is the arming condition. The tap is a shortcut to a chunk; the
   * two buttons are the keyboard's way to the same chunk. So the container keeps
   * no role and no aria either: it is text that accepts a shortcut, not a
   * control, and announcing it as a control would be a lie to a screen reader.
   */
  const handleReadFromTap = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!readFromTapArmed || !voiceSpeechManagerRef.current) {
      return
    }

    // Starting speech needs a real user gesture - that is what satisfies iOS, and
    // a tap is one. handlePlayPause refuses an untrusted event for the same
    // reason, and so must the second way into the same engine.
    if (event.isTrusted !== true) {
      return
    }

    // Selecting a passage to copy ends in a click. That click must not also start
    // reading, or copying anything would be impossible without hearing it.
    const selection = window.getSelection()
    if (selection && !selection.isCollapsed) {
      return
    }

    // Only a tap that landed on a chunk counts. The runs between chunks are
    // whitespace, page separators and blank lines; resolving one of those to the
    // chunk nearest it would turn a stray tap in a margin into speech.
    const chunkElement = (event.target as Element | null)?.closest?.('[data-chunk-index]')
    if (!chunkElement) {
      return
    }

    const chunkIndex = Number(chunkElement.getAttribute('data-chunk-index'))
    if (!Number.isInteger(chunkIndex)) {
      return
    }

    // The bar stays as it is: it is already open, since that is what armed this,
    // and closing it remains the reader's decision alone.
    voiceSpeechManagerRef.current.startFromChunk(chunkIndex)
  }

  /** Closing the bar stops the speech: leaving it running with no visible transport
   * control is how a reader ends up with a voice they cannot pause. */
  const handleCloseSpeechBar = () => {
    voiceSpeechManagerRef.current?.stop()
    setShowSpeechBar(false)
  }

  const handleStop = () => {
    if (voiceSpeechManagerRef.current) {
      voiceSpeechManagerRef.current.stop()
    } else {
      speechSynthesis.cancel()
      setIsPlaying(false)
      setCurrentWordIndex(-1)
      setCurrentSentenceIndex(-1)
      setIsPaused(false)
    }
  }

  // Stop speech when navigating away (back button, logo click, or browser refresh)
  const stopSpeechOnNavigation = useCallback(() => {
    if (voiceSpeechManagerRef.current) {
      voiceSpeechManagerRef.current.stop()
    }
  }, [])

  // Handle back button click - show confirmation dialog
  const handleBackToHomeWithStop = useCallback(() => {
    pendingNavigationRef.current = 'back'
    setShowLeaveConfirmation(true)
  }, [])

  // Handle logo click - show confirmation dialog
  const handleNavigateToHomeWithStop = useCallback(() => {
    pendingNavigationRef.current = 'home'
    setShowLeaveConfirmation(true)
  }, [])

  // Handle confirmed navigation after user confirms in dialog
  const handleConfirmedNavigation = useCallback(() => {
    stopSpeechOnNavigation()
    setShowLeaveConfirmation(false)
    // Go back in history to remove the dummy state we pushed
    window.history.back()
    if (pendingNavigationRef.current === 'back') {
      onBackToHome()
    } else if (pendingNavigationRef.current === 'home') {
      onNavigateToHome()
    }
    pendingNavigationRef.current = null
  }, [stopSpeechOnNavigation, onBackToHome, onNavigateToHome])

  // Handle cancel - close dialog and stay on page
  const handleCancelNavigation = useCallback(() => {
    setShowLeaveConfirmation(false)
    pendingNavigationRef.current = null
  }, [])

  // Intercept browser back button
  useEffect(() => {
    // Push a dummy state to history so we can intercept the back button
    window.history.pushState({ dyslexa: 'text-display' }, '', window.location.href)
    
    const handlePopState = (event: PopStateEvent) => {
      // Prevent actual navigation by pushing state back
      window.history.pushState({ dyslexa: 'text-display' }, '', window.location.href)
      // Show confirmation dialog
      pendingNavigationRef.current = 'back'
      setShowLeaveConfirmation(true)
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  // Stop speech on browser refresh/unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Show browser confirmation dialog only if user hasn't downloaded the text yet
      if (!hasDownloadedRef.current) {
        e.preventDefault()
        e.returnValue = '' // Required for Chrome/Edge
      }
      
      // Force stop speech immediately through the manager when available
      if (voiceSpeechManagerRef.current) {
        voiceSpeechManagerRef.current.stop()
      } else {
        speechSynthesis.cancel()
      }
      stopSpeechOnNavigation()
    }

    const handlePageHide = () => {
      // Force stop speech immediately
      if (voiceSpeechManagerRef.current) {
        voiceSpeechManagerRef.current.stop()
      } else {
        speechSynthesis.cancel()
      }
      stopSpeechOnNavigation()
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // When tab becomes hidden, pause speech instead of stopping it
        // This preserves the resume state and highlighting position
        if (voiceSpeechManagerRef.current && isPlaying && !isPaused) {
          console.log('Tab hidden: pausing speech to preserve resume state')
          setWasPlayingBeforeHidden(true)
          handlePlayPause() // This will pause the speech
        } else {
          setWasPlayingBeforeHidden(false)
        }
      } else {
        // When tab becomes visible again, we don't auto-resume
        // User needs to manually resume if they want to continue
        console.log('Tab visible: speech state preserved, user can resume manually')
        // Note: wasPlayingBeforeHidden can be used if we want to auto-resume in the future
      }
    }

    const handleUnload = () => {
      // Final attempt to stop speech
      if (voiceSpeechManagerRef.current) {
        voiceSpeechManagerRef.current.stop()
      } else {
        speechSynthesis.cancel()
      }
    }

    // Add multiple event listeners for better coverage
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('unload', handleUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handlePageHide)
      window.removeEventListener('unload', handleUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [stopSpeechOnNavigation, isPlaying, isPaused])

  const handleExport = async () => {
    const currentSettings = {
      fontFamily,
      fontSize,
      lineSpacing,
      textColor,
      backgroundColor,
      newSentenceNewLine,
    }

    // Awaited, caught, and confirmed out loud. None of that was true before, and
    // on a home-screen install there is no address bar to show a download, so a
    // working save and a dead button looked identical. DYS-027 / DYS-028.
    try {
      /*
       * Export what the document is, not what the editor is showing.
       *
       * While editing, currentDisplayText has its page tokens and emphasis stripped
       * for the textarea. The export button carries no editing guard, so exporting
       * mid-edit would have written an RTF with no section breaks and a page count
       * of one - a silent loss with nothing to connect it to the fact that the edit
       * box happened to be open.
       */
      const textToExport = editableMarkers
        ? fromEditableText(currentDisplayText, editableMarkers)
        : currentDisplayText
      const { outcome, filename } = await exportTextToFile(textToExport, currentSettings)

      // A dismissed share sheet or save dialog saved nothing, so it is neither a
      // success to announce nor a failure to report - and the tab-close warning
      // stays armed.
      if (outcome === 'cancelled') return

      setHasDownloaded(true) // Mark as downloaded to disable tab close warning
      if (outcome === 'shared') {
        toast.success(`Plik gotowy: ${filename}`, {
          description: 'Przekazano do wybranej aplikacji.',
        })
      } else if (outcome === 'saved') {
        // DYS-043. The user picked the folder themselves in the OS save dialog,
        // so naming one here would be worse than saying nothing: "Pobrane" is
        // the one place the file is now guaranteed *not* to be.
        toast.success(`Zapisano: ${filename}`, {
          description: 'Zapisano w wybranym folderze.',
        })
      } else {
        toast.success(`Zapisano: ${filename}`, {
          description: 'Plik znajdziesz w folderze Pobrane.',
        })
      }
    } catch (error) {
      // Logged as well as shown: until now a failed export surfaced only through
      // the global unhandled-rejection handler, and catching it here would
      // otherwise take that line out of the next bug report.
      logger.error('Text export failed', {
        error: error instanceof Error ? error.message : String(error),
        errorName: (error as { name?: string } | null)?.name,
      })
      toast.error('Nie udało się zapisać pliku', {
        description: 'Spróbuj ponownie lub zgłoś błąd.',
      })
    }
  }

  const formatTextWithNewlines = (text: string, applySentenceNewLines: boolean = true) => {
    let formatted = text

    if (newSentenceNewLine && applySentenceNewLines) {
      // Abbreviation-aware sentence splitting lives in a util so it can be
      // tested, and so it stays free of the lookbehind assertion that used to
      // crash this screen on iOS Safari before 16.4.
      formatted = breakSentencesAtAbbreviationSafePeriods(formatted)

      // Also handle ! and ? as sentence endings
      formatted = formatted.replace(/!\s+/g, "!\n")
      formatted = formatted.replace(/\?\s+/g, "?\n")
    }
    return formatted
  }

  // Render text with special markers (images, headings, bullets) as React components
  const renderFormattedText = (text: string, applySentenceNewLines: boolean = true): React.ReactNode => {
    const formattedText = formatTextWithNewlines(text, applySentenceNewLines)
    
    // Combined pattern to split text while keeping markers
    // Handles various formats:
    // - ### text ### or ### text or ### text #### (subheadings)
    // - ## text ## or ## text (main headings)
    // - [OBRAZ] ... [/OBRAZ] (image blocks)
    // - * text (bullet points)
    // Order matters: ### before ## to avoid partial matches
    const combinedPattern = /(\[OBRAZ\][\s\S]*?\[\/OBRAZ\]|^###\s*.+?(?:\s*#{2,})?$|^##\s*.+?(?:\s*##)?$|^\*\s+.+$)/gm
    
    const parts = formattedText.split(combinedPattern)
    
    return parts.map((part, index) => {
      if (!part) return null
      
      // Check if this is an image block
      const imageMatch = part.match(/^\[OBRAZ\]\s*([\s\S]*?)\s*\[\/OBRAZ\]$/)
      if (imageMatch) {
        return <ImageContentBlock key={index}>{imageMatch[1].trim()}</ImageContentBlock>
      }
      
      // Check if this is a subheading (### before ## to avoid partial matches)
      // Handles: ### text ###, ### text, ### text ####
      const subHeadingMatch = part.match(/^###\s*(.+?)(?:\s*#{2,})?$/)
      if (subHeadingMatch) {
        // Clean up any trailing # symbols from the content
        const content = subHeadingMatch[1].replace(/\s*#{2,}\s*$/, '').trim()
        return <SubHeading key={index}>{renderInlineEmphasis(content, `sh${index}`)}</SubHeading>
      }
      
      // Check if this is a main heading
      // Handles: ## text ##, ## text
      const mainHeadingMatch = part.match(/^##\s*(.+?)(?:\s*##)?$/)
      if (mainHeadingMatch) {
        const content = mainHeadingMatch[1].replace(/\s*##\s*$/, '').trim()
        return <MainHeading key={index}>{renderInlineEmphasis(content, `mh${index}`)}</MainHeading>
      }
      
      // Check if this is a bullet point
      const bulletMatch = part.match(/^\*\s+(.+)$/)
      if (bulletMatch) {
        return <BulletListItem key={index}>{renderInlineEmphasis(bulletMatch[1].trim(), `bl${index}`)}</BulletListItem>
      }
      
      // Regular text - emphasis is the only marker left to resolve inside it
      return <React.Fragment key={index}>{renderInlineEmphasis(part, `t${index}`)}</React.Fragment>
    }).filter(Boolean)
  }

  // Render text with page break separators
  const renderTextWithPageBreaks = (text: string) => {
    // Split by page break token
    const pages = text.split(PAGE_BREAK_TOKEN)
    
    // If no page breaks, just return formatted text
    if (pages.length === 1) {
      return renderFormattedText(text)
    }
    
    return pages.map((pageContent, pageIndex) => {
      const trimmedContent = pageContent.trim()
      
      return (
        <React.Fragment key={pageIndex}>
          {pageIndex > 0 && <PageBreakSeparator pageNumber={pageIndex} />}
          {renderFormattedText(trimmedContent)}
        </React.Fragment>
      )
    })
  }

  // Follow the highlight as it moves, whether it marks a word or a whole chunk.
  useEffect(() => {
    if (currentSentenceIndex < 0 || !highlightedWordRef.current) {
      return
    }

    const highlightElement = highlightedWordRef.current

    // Find the scrollable container (the div with overflow-y-auto)
    const scrollableContainer = highlightElement.closest('.overflow-y-auto')
    if (!scrollableContainer) {
      return
    }

    // Use getBoundingClientRect for accurate positioning
    const highlightRect = highlightElement.getBoundingClientRect()
    const containerRect = scrollableContainer.getBoundingClientRect()

    const highlightTopRelative =
      highlightRect.top - containerRect.top + scrollableContainer.scrollTop
    const highlightBottomRelative = highlightTopRelative + highlightRect.height
    const containerTop = scrollableContainer.scrollTop
    const containerBottom = containerTop + scrollableContainer.clientHeight

    // Scroll only when the highlight has left the window, so that following a word
    // does not drag the page along under a reader who is keeping up on their own.
    if (highlightTopRelative < containerTop) {
      scrollableContainer.scrollTo({
        top: highlightTopRelative - 50, // Add some padding
        behavior: 'smooth'
      })
    } else if (highlightBottomRelative > containerBottom) {
      scrollableContainer.scrollTo({
        top: highlightBottomRelative - scrollableContainer.clientHeight + 50,
        behavior: 'smooth'
      })
    }
  }, [currentSentenceIndex, currentWordIndex])

  /*
   * The chunks the highlight moves through.
   *
   * Memoised because playback re-renders this screen on every word, and this used
   * to re-split the entire document each time. The speech manager splits the same
   * text with the same function, so the two lists agree index for index - which is
   * what makes the word and chunk indices it reports meaningful here. Chunking
   * itself does not depend on the language; only the spoken text does.
   */
  const speechChunks = useMemo(
    () => findSentenceBoundaries(currentDisplayText),
    [currentDisplayText]
  )

  const darkBackground = isDarkBackground(backgroundColor)

  /*
   * Two strengths of the same highlight: the word carries the reading position,
   * the chunk keeps the reader's place in the paragraph around it.
   *
   * The external review asked for the word alone. The faint chunk stays because
   * losing your place is the failure this app exists to prevent, and at a tenth of
   * the strength it reads as context rather than as a second highlight. Both are
   * translucent overlays on the reader's own background, so all nine colour
   * presets keep their text colour untouched.
   */
  const wordHighlightColor = darkBackground ? 'rgba(255, 255, 255, 0.30)' : 'rgba(37, 99, 235, 0.26)'
  const chunkHighlightColor = darkBackground ? 'rgba(255, 255, 255, 0.09)' : 'rgba(37, 99, 235, 0.09)'

  /** One span per word, so the highlight can sit on just the word being spoken. */
  const renderChunkWords = (chunkText: string, activeWordIndex: number, keyPrefix: string) => {
    const words = splitDisplayWords(chunkText)

    return words.map((word, index) => {
      if (!word.text) {
        // A marker token: it keeps its index so the alignment stays true, but the
        // reader sees only the whitespace that surrounded it.
        return <React.Fragment key={`${keyPrefix}-m${index}`}>{word.gapBefore}</React.Fragment>
      }

      const isActive = index === activeWordIndex

      return (
        <React.Fragment key={`${keyPrefix}-w${index}`}>
          {word.gapBefore}
          <span
            ref={isActive ? highlightedWordRef : null}
            style={{
              ...SPEECH_WORD_STYLE,
              backgroundColor: isActive ? wordHighlightColor : 'transparent',
            }}
          >
            {word.emphasized ? (
              <Emphasis>{renderAddresses(word.text, `${keyPrefix}-w${index}`)}</Emphasis>
            ) : (
              renderAddresses(word.text, `${keyPrefix}-w${index}`)
            )}
          </span>
        </React.Fragment>
      )
    })
  }

  /**
   * A chunk with per-word spans inside whatever block the source made of it.
   *
   * The block markers are matched here rather than delegated to
   * renderFormattedText because that helper resolves them by rebuilding the text,
   * which would take the word spans with it.
   *
   * Image blocks used to be the exception, handed back to renderFormattedText
   * whole. They no longer are, and could not be: the chunker splits a
   * `[OBRAZ] ... [/OBRAZ]` block at the sentence inside it, so a chunk holds one
   * marker and not the other, matched nothing, and the reader was shown the raw
   * marker. The block is now reassembled a level up, as a run that wraps the
   * chunks inside it (chunked-text-runs.ts), and the marker left in the chunk's
   * own text is hidden by splitDisplayWords like any other markup token.
   */
  const renderChunkWithWordHighlight = (
    chunkText: string,
    activeWordIndex: number,
    keyPrefix: string
  ): React.ReactNode => {
    const words = renderChunkWords(chunkText, activeWordIndex, keyPrefix)

    if (/^###/.test(chunkText)) return <SubHeading>{words}</SubHeading>
    if (/^##/.test(chunkText)) return <MainHeading>{words}</MainHeading>
    if (/^\*\s/.test(chunkText)) return <BulletListItem>{words}</BulletListItem>

    return words
  }

  /*
   * Chunks that are not being spoken, rendered once and reused.
   *
   * Their markup cannot change between two words of the same document, and holding
   * the same element references lets React skip those subtrees entirely - without
   * this, every boundary event re-ran the marker parsing for every chunk on every
   * page.
   */
  const inactiveChunkContent = useMemo(
    // The markers are stripped rather than rendered: see
    // renderChunkWithWordHighlight above, which hides the same ones token by
    // token on the chunk that is being spoken.
    () => speechChunks.map((chunk) => renderFormattedText(stripImageBlockMarkers(chunk.text), false)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [speechChunks, newSentenceNewLine]
  )

  /*
   * The document as the pane renders it: the chunks, and everything between them.
   *
   * Memoised alongside the chunk list for the same reason it is - playback
   * re-renders this screen on every word, and this walks the whole document.
   */
  const chunkedTextRuns = useMemo(
    () =>
      buildChunkedTextRuns(currentDisplayText, speechChunks, {
        sentenceOnNewLine: newSentenceNewLine,
      }),
    [currentDisplayText, speechChunks, newSentenceNewLine]
  )

  /** One chunk: the highlight target, and the thing a tap resolves to. */
  const renderChunkRun = (chunkIndex: number, key: string): React.ReactNode => {
    const chunk = speechChunks[chunkIndex]
    if (!chunk) return null

    const isActiveChunk = chunkIndex === currentSentenceIndex

    /*
     * The word to mark, as an index within this chunk.
     *
     * -1 on the engines that report no word boundaries at all, and the reader
     * gets the chunk highlight alone - which is why chunks are now phrase-sized
     * rather than whole sentences.
     */
    const activeWordIndex =
      isActiveChunk && wordHighlightAvailable && currentWordIndex >= 0
        ? currentWordIndex - chunk.startWordIndex
        : -1

    return (
      <span
        key={key}
        // What the delegated click handler on the container resolves a tap to.
        data-chunk-index={chunkIndex}
        // Without a word to point at, the scroll follows the chunk instead.
        ref={isActiveChunk && activeWordIndex < 0 ? highlightedWordRef : null}
        style={{
          ...SPEECH_CHUNK_STYLE,
          backgroundColor: isActiveChunk ? chunkHighlightColor : 'transparent',
          // The affordance appears only while the gesture is armed, so a pointer
          // never promises something a tap would not do. It sits on the chunks
          // rather than on the container because the whitespace between them is
          // not a target.
          cursor: readFromTapArmed ? 'pointer' : undefined,
        }}
      >
        {isActiveChunk
          ? renderChunkWithWordHighlight(chunk.text, activeWordIndex, `c${chunkIndex}`)
          : inactiveChunkContent[chunkIndex]}
      </span>
    )
  }

  const renderChunkedRun = (run: ChunkedTextRun, key: string): React.ReactNode => {
    if (run.kind === 'text') {
      return <React.Fragment key={key}>{run.text}</React.Fragment>
    }

    if (run.kind === 'pageBreak') {
      return <PageBreakSeparator key={key} pageNumber={run.pageNumber} />
    }

    if (run.kind === 'imageBlock') {
      return (
        <ImageContentBlock key={key}>
          {run.runs.map((nested, index) => renderChunkedRun(nested, `${key}-${index}`))}
        </ImageContentBlock>
      )
    }

    return renderChunkRun(run.index, key)
  }

  /*
   * The whole reading pane, chunk by chunk.
   *
   * This is the only renderer now: it runs on an idle document as well as a
   * playing one, because a tap can only reach a chunk that has been rendered as
   * one. Idle is unchanged to look at - both highlight colours resolve to
   * transparent when nothing is playing - but everything the unchunked renderer
   * used to do to the text between chunks has to be done here instead, which is
   * what the run builder is for. See chunked-text-runs.ts.
   *
   * The walk moved there for a second reason: it rendered the text before each
   * chunk and never the text after the last one, so a document's tail - on a
   * PDF, the closing page separator - was dropped.
   *
   * One thing does change, and it is the better half of the trade. A chunk is an
   * inline box, and Chromium breaks a line slightly differently across one, so a
   * paragraph can hyphenate at a different word than it did with the text in one
   * piece. It no longer changes when playback starts, though - which is what it
   * used to do, reflowing the paragraph under a reader at the exact moment they
   * began following it.
   */
  const renderTextWithSentenceHighlight = () => {
    if (speechChunks.length === 0) {
      return renderTextWithPageBreaks(currentDisplayText)
    }

    return chunkedTextRuns.map((run, runIndex) => renderChunkedRun(run, `run-${runIndex}`))
  }

  const fontOptions = FONT_OPTIONS

  const handleEditClick = () => {
    const editable = toEditableText(currentDisplayText)
    setOriginalTextOnEditStart(currentDisplayText)
    setEditableMarkers(editable)
    setCurrentDisplayText(editable.text)
    setIsEditing(true)
    setShowSettings(false)
    handleStop()
  }

  const handleSaveEdit = () => {
    if (editableMarkers) {
      setCurrentDisplayText(fromEditableText(currentDisplayText, editableMarkers))
      setEditableMarkers(null)
    }
    setIsEditing(false)
  }

  const handleCancelEdit = () => {
    setCurrentDisplayText(originalTextOnEditStart)
    setEditableMarkers(null)
    setIsEditing(false)
  }

  const fallbackPagesCount = ocrSummary?.fallbackPageIndices?.length ?? (ocrProvider === 'tesseract' ? 1 : 0)
  const missingPagesCount = ocrSummary?.missingPageIndices?.length ?? 0
  const fallbackPagesLabel = ocrSummary?.fallbackPageIndices?.map((index) => index + 1).join(', ')
  const missingPagesLabel = ocrSummary?.missingPageIndices?.map((index) => index + 1).join(', ')
  const OCR_FALLBACK_BANNER_ENABLED = process.env.NEXT_PUBLIC_OCR_FALLBACK_BANNER_ENABLED !== 'false'
  const showOcrNotice = OCR_FALLBACK_BANNER_ENABLED && showTesseractNotice && (fallbackPagesCount > 0 || missingPagesCount > 0 || ocrProvider === 'tesseract')

  return (
    <div 
      className="flex flex-col"
      style={{ 
        backgroundColor,
        height: 'calc(var(--vh, 1vh) * 100)' // Use CSS custom property for iOS Safari
      }}
    >
      {/* Header - Fixed at top */}
      <header className="fixed top-0 left-0 right-0 z-30 bg-[#ffffff] md:bg-[#2B0448] border-b px-4 py-4 rounded-b-3xl md:rounded-b-none">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={isEditing ? handleCancelEdit : handleBackToHomeWithStop}
            className="flex items-center space-x-2 md:order-last text-[#1E3144] md:text-[#FBF8FF] hover:text-[#1E3144] md:hover:text-[#FBF8FF] hover:bg-[#1E3144]/10 md:hover:bg-[#FBF8FF]/10"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{isEditing ? "Anuluj" : "Wstecz"}</span>
          </Button>

          <div className="flex items-center gap-5 md:order-2">
            {/* Edit button - toggles between edit/save mode */}
            <button
              onClick={isEditing ? handleSaveEdit : handleEditClick}
              aria-label={isEditing ? "Zapisz zmiany" : "Edytuj tekst"}
              className={`p-1 hover:opacity-70 transition-opacity ${
                isEditing 
                  ? "text-green-600 md:text-green-400" 
                  : "text-[#1E3144] md:text-[#FBF8FF]"
              }`}
            >
              {isEditing ? <Check className="h-5 w-5" /> : <Pencil className="h-5 w-5" />}
            </button>

            {/* Text format / Settings button */}
            <button
              onClick={() => setShowSettings(!showSettings)}
              aria-label="Dostosuj"
              className="p-1 text-[#1E3144] md:text-[#FBF8FF] hover:opacity-70 transition-opacity"
            >
              <TextFormatIcon className="size-5" />
            </button>

            {/* Speaker / Play button */}
            <button
              onClick={(e) => handlePlayPause(e)}
              aria-label={isPlaying ? "Pauza" : "Odtwórz"}
              className="p-1 text-[#1E3144] md:text-[#FBF8FF] hover:opacity-70 transition-opacity"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <SpeakerIcon className="size-5" />
              )}
            </button>

            {/* Download button */}
            <button
              onClick={handleExport}
              aria-label="Eksportuj"
              className="p-1 text-[#1E3144] md:text-[#FBF8FF] hover:opacity-70 transition-opacity"
            >
              <ExportIcon className="size-5" />
            </button>

            {/*
              * Help, last in the group and therefore nearest the right margin.
              *
              * It was first, on the reasoning that a reader who does not yet know
              * what the other icons do should not have to look past them. The
              * client tested it and asked for the end of the row instead, which
              * is also where a question mark conventionally sits, so that is
              * where it is. It opens an overlay rather than navigating, because
              * leaving this screen destroys the recognised text.
              */}
            <button
              onClick={() => setIsHelpOpen(true)}
              aria-label="Pomoc"
              className="p-1 text-[#1E3144] md:text-[#FBF8FF] hover:opacity-70 transition-opacity"
            >
              <HelpCircle className="h-5 w-5" />
            </button>
          </div>

          <div className="hidden md:flex items-center md:order-1">
            <button 
              onClick={handleNavigateToHomeWithStop}
              className="hover:opacity-80 transition-opacity cursor-pointer"
              aria-label="Go to home"
            >
              <Image
                src="/images/design-mode/logo.png"
                alt="Dyslexa"
                width={150}
                height={40}
                className="h-[40px] w-auto"
              />
            </button>
          </div>
        </div>
      </header>

      {/* OCR pipeline notice (fallback + missing pages) */}
      {showOcrNotice && (
        <div className="fixed top-20 left-0 right-0 z-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3 shadow-sm">
              <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1 min-w-0">
                {fallbackPagesCount > 0 && (
                  <>
                    <p className="text-sm text-amber-800 font-medium">
                      Użyto lokalnego rozpoznawania tekstu na {fallbackPagesCount} {fallbackPagesCount === 1 ? 'stronie' : 'stronach'}
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Powód: wyczerpano retry Gemini ({ocrSummary?.fallbackReason || 'Gemini retries exhausted'}).
                      {fallbackPagesLabel ? ` Strony: ${fallbackPagesLabel}.` : ''}
                    </p>
                  </>
                )}
                {missingPagesCount > 0 && (
                  <p className={`text-xs text-amber-700 ${fallbackPagesCount > 0 ? 'mt-2' : 'mt-0.5'}`}>
                    Wykryto brak treści na {missingPagesCount} {missingPagesCount === 1 ? 'stronie' : 'stronach'}.
                    {missingPagesLabel ? ` Strony: ${missingPagesLabel}.` : ''}
                  </p>
                )}
                {fallbackPagesCount === 0 && missingPagesCount === 0 && (
                  <p className="text-xs text-amber-700 mt-0.5">
                    OCR online był chwilowo niedostępny. Jakość tekstu może być niższa.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {onRetryOcr && (
                  <button
                    onClick={onRetryOcr}
                    className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-800 px-3 py-1.5 rounded-md font-medium transition-colors"
                  >
                    Spróbuj ponownie
                  </button>
                )}
                <button
                  onClick={() => setShowTesseractNotice(false)}
                  className="text-amber-400 hover:text-amber-600 p-1 transition-colors"
                  aria-label="Zamknij"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Scrollable area */}
      <div className={`flex-1 flex overflow-hidden ${showOcrNotice ? 'pt-32' : 'pt-10'}`}>
        {/*
          * min-w-0: a flex item will not shrink below its own min-content width,
          * and with pre-wrap text that width is the longest unbreakable run - a URL
          * or a 23-digit number in the OCR output. Without this the column grows
          * past the viewport on a phone, the overflow-hidden above clips it, and
          * the text stops reflowing on font-size changes.
          */}
        <div className="flex-1 flex flex-col h-full min-w-0">
          {/* Text Display - Scrollable */}
          <div 
            className={`flex-1 px-2 py-6 md:p-6 ${showSettings ? 'overflow-hidden pointer-events-none touch-none' : 'overflow-y-auto'}`}
            style={{ height: 'calc(var(--vh, 1vh) * 100 - 5rem)' }}
          >
            <div className="max-w-4xl mx-auto">
              <Card className="bg-transparent border-none shadow-none">
                <CardContent
                  className="px-3 py-6 md:p-8"
                  style={{
                    color: textColor,
                    fontSize: `${fontSize}px`,
                    lineHeight: lineSpacing / 100,
                    fontFamily: fontFamily,
                    letterSpacing: FIXED_TEXT_STYLES.letterSpacing,
                    wordSpacing: FIXED_TEXT_STYLES.wordSpacing,
                    maxWidth: '100%',
                    overflow: 'hidden',
                  }}
                >
                  {isEditing ? (
                    <textarea
                      className="w-full min-h-[500px] p-4 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary px-2 py-2"
                      value={currentDisplayText}
                      onChange={(e) => {
                        const newText = e.target.value
                        setCurrentDisplayText(newText)
                        if (voiceSpeechManagerRef.current) {
                          voiceSpeechManagerRef.current.updateDisplayText(newText)
                        }
                      }}
                      style={{
                        color: textColor,
                        fontSize: `${fontSize}px`,
                        lineHeight: lineSpacing / 100,
                        fontFamily: fontFamily,
                        letterSpacing: FIXED_TEXT_STYLES.letterSpacing,
                        wordSpacing: FIXED_TEXT_STYLES.wordSpacing,
                        backgroundColor: "transparent",
                        height: "calc(var(--vh, 1vh) * 100 - 280px)", // Adjusted for fixed header with iOS Safari fix
                      }}
                      aria-label="Edytowalny tekst"
                    />
                  ) : (
                    <div 
                      ref={textContainerRef}
                      onClick={handleReadFromTap}
                      className="whitespace-pre-wrap"
                      style={{
                        scrollBehavior: 'smooth',
                        /*
                         * anywhere, not break-word: break-word breaks a long word only
                         * once it already overflows a line, and leaves the min-content
                         * width of the box as wide as that word. anywhere shrinks it too,
                         * so a scanned URL wraps instead of forcing the page wide.
                         */
                        overflowWrap: 'anywhere',
                        wordBreak: 'normal',
                        hyphens: 'auto',
                      }}
                    >
                      {renderTextWithSentenceHighlight()}
                    </div>
                  )}
                  {/* Room for the speech bar, so it never covers the last lines. Sized
                      for the single-row bar: it was h-40 while the bar was two rows,
                      and that much empty space at the end of a document read as a
                      broken page. */}
                  {showSpeechBar && !isEditing && <div aria-hidden="true" className="h-24" />}
                </CardContent>
                {isEditing && (
                  <div className="flex justify-end gap-4 mt-4 px-8 pb-8">
                    <Button onClick={handleCancelEdit} variant="outline" className="rounded-full bg-transparent">
                      Anuluj
                    </Button>
                    <Button
                      onClick={handleSaveEdit}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
                    >
                      Zapisz
                    </Button>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 opacity-0"
            onClick={() => setShowSettings(false)}
          ></div>
          <div className="fixed top-0 right-0 w-80 h-full bg-card border-l p-6 overflow-y-auto shadow-lg z-50 space-y-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSettings(false)}
              className="absolute top-4 right-4"
              aria-label="Zamknij dostosowanie"
            >
              <X className="h-5 w-5" />
            </Button>
            <h3 className="text-lg flex items-center mr-10 text-muted-foreground font-bold">
              <TextFormatIcon className="mr-2 size-6" />
              {"Dostosuj"}
            </h3>

            {/* Font Type Selection */}
            <div className="my-px space-y-2">
              <label className="flex items-center font-medium text-base text-foreground pt-0">Czcionka</label>
              <div className="flex overflow-x-auto space-x-2 pb-2">
                {fontOptions.map((font) => (
                  <button
                    key={font.name}
                    className={`flex-shrink-0 flex flex-col items-center justify-start gap-1 pt-2 px-0 rounded-md cursor-pointer transition-colors w-16 min-h-[96px] ${
                      fontFamily === font.value
                        ? "bg-muted/40"
                        : "hover:bg-muted/60"
                    }`}
                    onClick={() => setFontFamily(font.value)}
                  >
                    <div
                      className={`w-10 h-10 flex items-center justify-center text-2xl font-bold border border-border rounded shrink-0 ${
                        fontFamily === font.value ? "bg-muted" : "bg-card"
                      }`}
                      style={{ fontFamily: font.value }}
                    >
                      A
                    </div>
                    <span className="h-8 flex items-start justify-center text-xs text-muted-foreground text-center leading-tight break-words">
                      {font.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4 my-4 pt-6">
              <label className="flex items-center font-medium text-base text-foreground my-0 pb-0 mt-0 mb-1">
                Rozmiar
              </label>

              {/* Font size labels */}
              <div className="relative px-2">
                <div className="flex justify-between items-center mb-2">
                  {fontSizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setFontSize(size)}
                      className={`text-sm transition-all ${
                        fontSize === size ? "font-bold text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {fontSize === size ? `${size}px` : size}
                    </button>
                  ))}
                </div>

                {/* Custom slider track */}
                <div className="relative h-[2px] bg-[#ECECEF] rounded-full">
                  {fontSizes.map((size, index) => (
                    <div
                      key={size}
                      className="absolute top-1/2 -translate-y-1/2 w-[2px] h-[8px] bg-[#ECECEF]"
                      style={{
                        left: `${(index / (fontSizes.length - 1)) * 100}%`,
                      }}
                    />
                  ))}

                  <div
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 bg-[#9EA5B7] border-2 border-[#6D727F] rounded-full shadow-md cursor-pointer transition-all hover:scale-110 border-slate-500 size-6 border-solid"
                    style={{
                      left: `${(paletteIndex(fontSizes, fontSize) / (fontSizes.length - 1)) * 100}%`,
                    }}
                  />

                  {/* Invisible input for accessibility with enlarged hit area */}
                  <input
                    type="range"
                    min={0}
                    max={fontSizes.length - 1}
                    value={paletteIndex(fontSizes, fontSize)}
                    onChange={(e) => setFontSize(fontSizes[Number.parseInt(e.target.value)])}
                    className="absolute inset-0 w-full h-10 opacity-0 cursor-pointer"
                    aria-label="Font size"
                  />
                </div>
              </div>
            </div>

            {/* Line Spacing */}
            <div className="space-y-4 my-4 pt-6">
              <label className="flex items-center font-medium text-base text-foreground my-0 pb-0 mt-0 mb-1">
                Odstęp
              </label>

              {/* Line spacing labels */}
              <div className="relative px-2">
                <div className="flex justify-between items-center mb-2">
                  {lineSpacings.map((spacing) => (
                    <button
                      key={spacing}
                      onClick={() => setLineSpacing(spacing)}
                      className={`text-sm transition-all ${
                        lineSpacing === spacing ? "font-bold text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {lineSpacing === spacing ? `${spacing}%` : spacing}
                    </button>
                  ))}
                </div>

                {/* Custom slider track */}
                <div className="relative h-[2px] bg-[#ECECEF] rounded-full">
                  {lineSpacings.map((spacing, index) => (
                    <div
                      key={spacing}
                      className="absolute top-1/2 -translate-y-1/2 w-[2px] h-[8px] bg-[#ECECEF]"
                      style={{
                        left: `${(index / (lineSpacings.length - 1)) * 100}%`,
                      }}
                    />
                  ))}

                  <div
                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 bg-[#9EA5B7] border-2 border-[#6D727F] rounded-full shadow-md cursor-pointer transition-all hover:scale-110 size-6 border-solid"
                    style={{
                      left: `${(paletteIndex(lineSpacings, lineSpacing) / (lineSpacings.length - 1)) * 100}%`,
                    }}
                  />

                  {/* Invisible input for accessibility with enlarged hit area */}
                  <input
                    type="range"
                    min={0}
                    max={lineSpacings.length - 1}
                    value={paletteIndex(lineSpacings, lineSpacing)}
                    onChange={(e) => setLineSpacing(lineSpacings[Number.parseInt(e.target.value)])}
                    className="absolute inset-0 w-full h-10 opacity-0 cursor-pointer"
                    aria-label="Line spacing"
                  />
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="space-y-3 my-8 pt-6">
              <label
                id="color-presets-label"
                className="flex items-center font-medium text-base text-foreground my-0 pb-0 mt-0 mb-2"
              >
                Kolory
              </label>

              {/*
                DYS-047. Nine curated pairs as tiles, in a radio group.

                One setting, nine options, exactly one of them true: that is a
                radio group, and the roving tabIndex below is what keeps it to a
                single tab stop. It departs from both of its neighbours - the font
                strip is a row of plain buttons, the size and spacing controls are
                range inputs - and it is worth the departure, because nine tab
                stops in the middle of the drawer, for one choice, is a real
                regression for anyone reading this app by keyboard, and the two
                sliders this replaces were one stop each.

                Selection cannot be shown the house way. The drawer marks a chosen
                control with a grey border and a grey fill, and both of those
                disappear on a tile that is itself near-black, white or light grey.
                So it is shown twice over: a ring offset clear of the swatch, which
                lands on the drawer's own background and reads whatever the tile
                holds, and a tick inside drawn in the preset's own font colour,
                which is guaranteed to contrast with the preset's own background -
                that being the entire point of a pair. Two signals, and neither of
                them colour alone (WCAG 1.4.1).
              */}
              <div
                role="radiogroup"
                aria-labelledby="color-presets-label"
                className="grid grid-cols-3 gap-3"
              >
                {COLOR_PRESETS.map((preset, index) => {
                  const selected = index === selectedColorPresetIndex

                  return (
                    <button
                      key={preset.backgroundColor}
                      ref={(node) => {
                        colorPresetRefs.current[index] = node
                      }}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      aria-label={preset.label}
                      tabIndex={selected ? 0 : -1}
                      onClick={() => selectColorPreset(index)}
                      onKeyDown={(event) => handleColorPresetKeyDown(event, index)}
                      className={`relative flex aspect-square w-full items-center justify-center rounded-md border text-2xl font-bold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3144] focus-visible:ring-offset-2 focus-visible:ring-offset-card ${
                        selected
                          ? "border-transparent ring-2 ring-[#6D727F] ring-offset-2 ring-offset-card"
                          : "border-border hover:scale-105"
                      }`}
                      style={{ backgroundColor: preset.backgroundColor, color: preset.textColor }}
                    >
                      Aa
                      {selected && (
                        <Check className="absolute top-1 right-1 size-4" aria-hidden="true" />
                      )}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Text Options */}
            <div className="space-y-3">
              {/*
                A native checkbox is drawn by the platform, which is why this one
                never matched the drawer around it: a blue system tick on Windows,
                a grey one on iOS, and neither of them in the line weight every
                other control here is drawn in. The input stays and does all the
                work - the label, the keyboard, the checked state - and is only
                moved out of sight, with a lucide box in its place. gap rather than
                space-x because an sr-only input is out of flow, so a sibling
                margin would indent the box the checkbox is supposed to start at,
                and peer-focus-visible because the focus ring would otherwise go
                out of sight with the input.
              */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newSentenceNewLine}
                  onChange={(e) => setNewSentenceNewLine(e.target.checked)}
                  className="sr-only peer"
                />
                <span className="rounded-sm text-[#6D727F] peer-focus-visible:ring-2 peer-focus-visible:ring-[#1E3144] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-card">
                  {newSentenceNewLine ? (
                    <SquareCheckBig className="size-6 shrink-0" aria-hidden="true" />
                  ) : (
                    <Square className="size-6 shrink-0" aria-hidden="true" />
                  )}
                </span>
                <span className="text-muted-foreground leading-7 text-base">Każde zdanie w osobnej linii</span>
              </label>
            </div>

            {/*
              * No voice picker, here or anywhere: the app chooses the voice itself
              * (scoreVoice in voice-speech.ts). It was briefly in the speech bar,
              * having been in this drawer before that, and both places shared the
              * same problem underneath - the reader was being asked to judge voices
              * they had no good way to compare. DYS-044.
              */}
          </div>
        </>
      )}

      {showSpeechBar && !isEditing && (
        <SpeechBar
          isPlaying={isPlaying}
          isPaused={isPaused}
          rate={resolveSpeechRate({ ttsRate })}
          onRateStep={(direction) => setTtsRate(stepSpeechRate(resolveSpeechRate({ ttsRate }), direction))}
          voices={availableVoices}
          voiceURI={ttsVoiceURI}
          onVoiceChange={setTtsVoiceURI}
          onPlayPause={handlePlayPause}
          onPrevious={handleSkipPrevious}
          onNext={handleSkipNext}
          onClose={handleCloseSpeechBar}
        />
      )}

      {/* Debug Log Panel - Hidden in production */}
      {/* <DebugLogPanel logs={debugLogs} /> */}

      {/* Leave Confirmation Dialog */}
      <AlertDialog open={showLeaveConfirmation} onOpenChange={setShowLeaveConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Czy na pewno chcesz wyjść?</AlertDialogTitle>
            <AlertDialogDescription>
              Twój przetworzony tekst zostanie utracony.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelNavigation}>Zostań</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedNavigation}>Wyjdź</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <HelpOverlay open={isHelpOpen} onOpenChange={setIsHelpOpen} />
    </div>
  )
}
